<script setup lang="ts">
import { computed, ref } from 'vue'

// v3.17.4 任务详情 v2: 块图 — 完成/进行中/待下载三态热力格。
// 数据: 后端 /live 的双位图 (base64, 每 bit 一块, 与 SegmentManager
// 分段拼接布局一致)。hover 显示块号/偏移/状态。

const props = defineProps<{
  completed: string // base64 位图
  inFlight: string // base64 位图
  blockLength: number
}>()

function bits(b64: string): boolean[] {
  if (!b64) return []
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
  const bytes: number[] = []
  let buf = 0
  let bitsN = 0
  for (const ch of b64) {
    const v = chars.indexOf(ch)
    if (v < 0) continue
    buf = (buf << 6) | v
    bitsN += 6
    while (bitsN >= 8) {
      bitsN -= 8
      bytes.push((buf >> bitsN) & 0xff)
    }
  }
  const out: boolean[] = []
  for (const byte of bytes) {
    for (let b = 7; b >= 0; b--) out.push(((byte >> b) & 1) === 1)
  }
  return out
}

interface Cell {
  idx: number
  state: 'done' | 'run' | 'pend'
  offsetMB: number
}

const cells = computed<Cell[]>(() => {
  const done = bits(props.completed)
  const run = bits(props.inFlight)
  if (done.length === 0) return []
  return done.map((d, i) => ({
    idx: i,
    state: d ? ('done' as const) : run[i] ? ('run' as const) : ('pend' as const),
    offsetMB: Math.round((i * props.blockLength) / (1024 * 1024)),
  }))
})

const hover = ref<Cell | null>(null)

const stateText: Record<Cell['state'], string> = {
  done: '已完成',
  run: '下载中',
  pend: '待下载',
}

const doneCount = computed(() => cells.value.filter((c) => c.state === 'done').length)
const runCount = computed(() => cells.value.filter((c) => c.state === 'run').length)
</script>

<template>
  <div>
    <div v-if="cells.length === 0" class="empty">暂无分块数据 (任务未开始或已完成)</div>
    <template v-else>
      <div class="bmap-head">
        <span class="cap">{{ cells.length }} 块 × {{ blockLength / (1024 * 1024) }} MB</span>
        <div class="legend">
          <span><i class="lg-done"></i>已完成 {{ doneCount }}</span>
          <span><i class="lg-run"></i>下载中 {{ runCount }}</span>
          <span><i class="lg-pend"></i>待下载 {{ cells.length - doneCount - runCount }}</span>
        </div>
      </div>
      <div class="bmap">
        <i
          v-for="c in cells"
          :key="c.idx"
          :class="c.state"
          @mouseenter="hover = c"
        ></i>
      </div>
      <div v-if="hover" class="tooltip">
        块 <b>#{{ hover.idx }}</b> · 偏移 {{ hover.offsetMB }} MB · {{ stateText[hover.state] }}
      </div>
    </template>
  </div>
</template>

<style scoped>
.empty {
  font-size: 12px;
  color: var(--t3, #94a3b8);
  padding: 8px 0;
}
.bmap-head { display: flex; gap: 12px; align-items: center; margin-bottom: 8px; }
.bmap-head .cap { font-size: 11.5px; color: var(--t2, #64748b); flex: 1; }
.legend { display: flex; gap: 12px; font-size: 11px; color: var(--t2, #64748b); }
.legend i { display: inline-block; width: 10px; height: 10px; border-radius: 2.5px; margin-right: 4px; vertical-align: -1px; }
.lg-done { background: #6366f1; } .lg-run { background: #fbbf24; } .lg-pend { background: #e2e8f0; }
.bmap { display: grid; grid-template-columns: repeat(auto-fill, minmax(10px, 1fr)); gap: 3px; }
.bmap i { display: block; aspect-ratio: 1; border-radius: 3px; background: #e2e8f0; cursor: crosshair; }
.bmap i.d { background: #6366f1; }
.bmap i.r { background: #fbbf24; animation: pv-pulse 1s ease-in-out infinite; }
@keyframes pv-pulse { 50% { opacity: 0.35 } }
.bmap i:hover { outline: 1.5px solid #0f172a; }
.tooltip { position: relative; margin-top: -32px; margin-left: 40%; width: max-content; z-index: 5;
  background: #0f172a; color: #e2e8f0; font-size: 11.5px; padding: 6px 10px; border-radius: 7px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.35); pointer-events: none; }
.tooltip b { color: #fbbf24; }
</style>
